/**
 * Plan Templates for Omniapps Planner
 * Each template contains metadata, sections, and wizard questions
 */

export const PLAN_CATEGORIES = {
  CORPORATE_STRATEGIC: 'corporate_strategic',
  FUNCTIONAL_TACTICAL: 'functional_tactical',
  CONTINGENCY_RISK: 'contingency_risk',
};

export const CATEGORY_INFO = {
  [PLAN_CATEGORIES.CORPORATE_STRATEGIC]: {
    id: PLAN_CATEGORIES.CORPORATE_STRATEGIC,
    nameKey: 'templates.categories.corporateStrategic',
    descriptionKey: 'templates.categories.corporateStrategicDesc',
    icon: 'Building2',
  },
  [PLAN_CATEGORIES.FUNCTIONAL_TACTICAL]: {
    id: PLAN_CATEGORIES.FUNCTIONAL_TACTICAL,
    nameKey: 'templates.categories.functionalTactical',
    descriptionKey: 'templates.categories.functionalTacticalDesc',
    icon: 'Target',
  },
  [PLAN_CATEGORIES.CONTINGENCY_RISK]: {
    id: PLAN_CATEGORIES.CONTINGENCY_RISK,
    nameKey: 'templates.categories.contingencyRisk',
    descriptionKey: 'templates.categories.contingencyRiskDesc',
    icon: 'Shield',
  },
};

export const PLAN_TEMPLATES = [
  // Corporate & Strategic Planning
  {
    id: 'business-plan',
    category: PLAN_CATEGORIES.CORPORATE_STRATEGIC,
    nameKey: 'templates.businessPlan.name',
    descriptionKey: 'templates.businessPlan.description',
    icon: 'Briefcase',
    sections: [
      { key: 'executive_summary', titleKey: 'templates.sections.executiveSummary' },
      { key: 'company_description', titleKey: 'templates.sections.companyDescription' },
      { key: 'market_analysis', titleKey: 'templates.sections.marketAnalysis' },
      { key: 'organization', titleKey: 'templates.sections.organization' },
      { key: 'products_services', titleKey: 'templates.sections.productsServices' },
      { key: 'marketing_strategy', titleKey: 'templates.sections.marketingStrategy' },
      { key: 'financial_projections', titleKey: 'templates.sections.financialProjections' },
      { key: 'funding_request', titleKey: 'templates.sections.fundingRequest' },
    ],
    questions: [
      { id: 'business_name', type: 'text', questionKey: 'templates.businessPlan.questions.businessName', required: true },
      { id: 'business_type', type: 'select', questionKey: 'templates.businessPlan.questions.businessType', options: ['startup', 'existing', 'franchise'], required: true },
      { id: 'industry', type: 'text', questionKey: 'templates.businessPlan.questions.industry', required: true },
      { id: 'target_market', type: 'textarea', questionKey: 'templates.businessPlan.questions.targetMarket', required: true },
      { id: 'unique_value', type: 'textarea', questionKey: 'templates.businessPlan.questions.uniqueValue', required: true },
      { id: 'funding_needed', type: 'select', questionKey: 'templates.businessPlan.questions.fundingNeeded', options: ['yes', 'no', 'maybe'], required: true },
      { id: 'funding_amount', type: 'text', questionKey: 'templates.businessPlan.questions.fundingAmount', conditional: { field: 'funding_needed', value: 'yes' } },
      { id: 'timeline', type: 'select', questionKey: 'templates.businessPlan.questions.timeline', options: ['1year', '3year', '5year'], required: true },
    ],
  },
  {
    id: 'strategic-plan',
    category: PLAN_CATEGORIES.CORPORATE_STRATEGIC,
    nameKey: 'templates.strategicPlan.name',
    descriptionKey: 'templates.strategicPlan.description',
    icon: 'Compass',
    sections: [
      { key: 'vision_mission', titleKey: 'templates.sections.visionMission' },
      { key: 'current_state', titleKey: 'templates.sections.currentState' },
      { key: 'swot_analysis', titleKey: 'templates.sections.swotAnalysis' },
      { key: 'strategic_goals', titleKey: 'templates.sections.strategicGoals' },
      { key: 'initiatives', titleKey: 'templates.sections.initiatives' },
      { key: 'kpis', titleKey: 'templates.sections.kpis' },
      { key: 'implementation', titleKey: 'templates.sections.implementation' },
    ],
    questions: [
      { id: 'organization_name', type: 'text', questionKey: 'templates.strategicPlan.questions.organizationName', required: true },
      { id: 'current_vision', type: 'textarea', questionKey: 'templates.strategicPlan.questions.currentVision' },
      { id: 'current_mission', type: 'textarea', questionKey: 'templates.strategicPlan.questions.currentMission' },
      { id: 'planning_horizon', type: 'select', questionKey: 'templates.strategicPlan.questions.planningHorizon', options: ['3year', '5year', '10year'], required: true },
      { id: 'key_challenges', type: 'textarea', questionKey: 'templates.strategicPlan.questions.keyChallenges', required: true },
      { id: 'growth_areas', type: 'textarea', questionKey: 'templates.strategicPlan.questions.growthAreas', required: true },
      { id: 'competitive_position', type: 'textarea', questionKey: 'templates.strategicPlan.questions.competitivePosition' },
    ],
  },
  {
    id: 'growth-plan',
    category: PLAN_CATEGORIES.CORPORATE_STRATEGIC,
    nameKey: 'templates.growthPlan.name',
    descriptionKey: 'templates.growthPlan.description',
    icon: 'TrendingUp',
    sections: [
      { key: 'current_position', titleKey: 'templates.sections.currentPosition' },
      { key: 'growth_objectives', titleKey: 'templates.sections.growthObjectives' },
      { key: 'market_opportunities', titleKey: 'templates.sections.marketOpportunities' },
      { key: 'expansion_strategy', titleKey: 'templates.sections.expansionStrategy' },
      { key: 'resource_requirements', titleKey: 'templates.sections.resourceRequirements' },
      { key: 'financial_plan', titleKey: 'templates.sections.financialPlan' },
      { key: 'risk_assessment', titleKey: 'templates.sections.riskAssessment' },
    ],
    questions: [
      { id: 'company_name', type: 'text', questionKey: 'templates.growthPlan.questions.companyName', required: true },
      { id: 'current_revenue', type: 'text', questionKey: 'templates.growthPlan.questions.currentRevenue' },
      { id: 'growth_type', type: 'multiselect', questionKey: 'templates.growthPlan.questions.growthType', options: ['geographic', 'product', 'market', 'acquisition'], required: true },
      { id: 'target_markets', type: 'textarea', questionKey: 'templates.growthPlan.questions.targetMarkets', required: true },
      { id: 'growth_timeline', type: 'select', questionKey: 'templates.growthPlan.questions.growthTimeline', options: ['1year', '2year', '3year', '5year'], required: true },
      { id: 'investment_capacity', type: 'textarea', questionKey: 'templates.growthPlan.questions.investmentCapacity' },
    ],
  },
  {
    id: 'succession-plan',
    category: PLAN_CATEGORIES.CORPORATE_STRATEGIC,
    nameKey: 'templates.successionPlan.name',
    descriptionKey: 'templates.successionPlan.description',
    icon: 'Users',
    sections: [
      { key: 'current_leadership', titleKey: 'templates.sections.currentLeadership' },
      { key: 'critical_roles', titleKey: 'templates.sections.criticalRoles' },
      { key: 'talent_assessment', titleKey: 'templates.sections.talentAssessment' },
      { key: 'development_plans', titleKey: 'templates.sections.developmentPlans' },
      { key: 'transition_timeline', titleKey: 'templates.sections.transitionTimeline' },
      { key: 'knowledge_transfer', titleKey: 'templates.sections.knowledgeTransfer' },
    ],
    questions: [
      { id: 'organization_name', type: 'text', questionKey: 'templates.successionPlan.questions.organizationName', required: true },
      { id: 'succession_type', type: 'select', questionKey: 'templates.successionPlan.questions.successionType', options: ['planned_retirement', 'emergency', 'growth', 'restructuring'], required: true },
      { id: 'key_positions', type: 'textarea', questionKey: 'templates.successionPlan.questions.keyPositions', required: true },
      { id: 'timeline', type: 'select', questionKey: 'templates.successionPlan.questions.timeline', options: ['immediate', '1year', '3year', '5year'], required: true },
      { id: 'internal_candidates', type: 'select', questionKey: 'templates.successionPlan.questions.internalCandidates', options: ['yes', 'some', 'no'], required: true },
    ],
  },

  // Functional & Tactical Planning
  {
    id: 'marketing-plan',
    category: PLAN_CATEGORIES.FUNCTIONAL_TACTICAL,
    nameKey: 'templates.marketingPlan.name',
    descriptionKey: 'templates.marketingPlan.description',
    icon: 'Megaphone',
    sections: [
      { key: 'executive_summary', titleKey: 'templates.sections.executiveSummary' },
      { key: 'situation_analysis', titleKey: 'templates.sections.situationAnalysis' },
      { key: 'target_audience', titleKey: 'templates.sections.targetAudience' },
      { key: 'marketing_objectives', titleKey: 'templates.sections.marketingObjectives' },
      { key: 'marketing_strategy', titleKey: 'templates.sections.marketingStrategy' },
      { key: 'tactics_channels', titleKey: 'templates.sections.tacticsChannels' },
      { key: 'budget', titleKey: 'templates.sections.budget' },
      { key: 'metrics_kpis', titleKey: 'templates.sections.metricsKpis' },
    ],
    questions: [
      { id: 'company_name', type: 'text', questionKey: 'templates.marketingPlan.questions.companyName', required: true },
      { id: 'product_service', type: 'textarea', questionKey: 'templates.marketingPlan.questions.productService', required: true },
      { id: 'target_audience', type: 'textarea', questionKey: 'templates.marketingPlan.questions.targetAudience', required: true },
      { id: 'marketing_budget', type: 'text', questionKey: 'templates.marketingPlan.questions.marketingBudget' },
      { id: 'channels', type: 'multiselect', questionKey: 'templates.marketingPlan.questions.channels', options: ['digital', 'social', 'content', 'email', 'paid', 'events', 'pr', 'traditional'], required: true },
      { id: 'goals', type: 'textarea', questionKey: 'templates.marketingPlan.questions.goals', required: true },
      { id: 'timeline', type: 'select', questionKey: 'templates.marketingPlan.questions.timeline', options: ['quarterly', 'annual', 'campaign'], required: true },
    ],
  },
  {
    id: 'product-launch-plan',
    category: PLAN_CATEGORIES.FUNCTIONAL_TACTICAL,
    nameKey: 'templates.productLaunchPlan.name',
    descriptionKey: 'templates.productLaunchPlan.description',
    icon: 'Rocket',
    sections: [
      { key: 'product_overview', titleKey: 'templates.sections.productOverview' },
      { key: 'market_research', titleKey: 'templates.sections.marketResearch' },
      { key: 'positioning', titleKey: 'templates.sections.positioning' },
      { key: 'go_to_market', titleKey: 'templates.sections.goToMarket' },
      { key: 'launch_timeline', titleKey: 'templates.sections.launchTimeline' },
      { key: 'success_metrics', titleKey: 'templates.sections.successMetrics' },
    ],
    questions: [
      { id: 'product_name', type: 'text', questionKey: 'templates.productLaunchPlan.questions.productName', required: true },
      { id: 'product_description', type: 'textarea', questionKey: 'templates.productLaunchPlan.questions.productDescription', required: true },
      { id: 'target_market', type: 'textarea', questionKey: 'templates.productLaunchPlan.questions.targetMarket', required: true },
      { id: 'launch_date', type: 'text', questionKey: 'templates.productLaunchPlan.questions.launchDate', required: true },
      { id: 'pricing_strategy', type: 'textarea', questionKey: 'templates.productLaunchPlan.questions.pricingStrategy' },
      { id: 'competitors', type: 'textarea', questionKey: 'templates.productLaunchPlan.questions.competitors' },
    ],
  },
  {
    id: 'sales-plan',
    category: PLAN_CATEGORIES.FUNCTIONAL_TACTICAL,
    nameKey: 'templates.salesPlan.name',
    descriptionKey: 'templates.salesPlan.description',
    icon: 'DollarSign',
    sections: [
      { key: 'sales_objectives', titleKey: 'templates.sections.salesObjectives' },
      { key: 'target_market', titleKey: 'templates.sections.targetMarket' },
      { key: 'sales_strategy', titleKey: 'templates.sections.salesStrategy' },
      { key: 'sales_process', titleKey: 'templates.sections.salesProcess' },
      { key: 'team_structure', titleKey: 'templates.sections.teamStructure' },
      { key: 'tools_resources', titleKey: 'templates.sections.toolsResources' },
      { key: 'forecasts', titleKey: 'templates.sections.forecasts' },
    ],
    questions: [
      { id: 'company_name', type: 'text', questionKey: 'templates.salesPlan.questions.companyName', required: true },
      { id: 'revenue_target', type: 'text', questionKey: 'templates.salesPlan.questions.revenueTarget', required: true },
      { id: 'sales_team_size', type: 'text', questionKey: 'templates.salesPlan.questions.salesTeamSize' },
      { id: 'sales_cycle', type: 'select', questionKey: 'templates.salesPlan.questions.salesCycle', options: ['short', 'medium', 'long', 'enterprise'], required: true },
      { id: 'target_customers', type: 'textarea', questionKey: 'templates.salesPlan.questions.targetCustomers', required: true },
      { id: 'sales_channels', type: 'multiselect', questionKey: 'templates.salesPlan.questions.salesChannels', options: ['direct', 'inside', 'partners', 'online', 'retail'], required: true },
    ],
  },
  {
    id: 'project-plan',
    category: PLAN_CATEGORIES.FUNCTIONAL_TACTICAL,
    nameKey: 'templates.projectPlan.name',
    descriptionKey: 'templates.projectPlan.description',
    icon: 'FolderKanban',
    sections: [
      { key: 'project_overview', titleKey: 'templates.sections.projectOverview' },
      { key: 'objectives_scope', titleKey: 'templates.sections.objectivesScope' },
      { key: 'deliverables', titleKey: 'templates.sections.deliverables' },
      { key: 'timeline_milestones', titleKey: 'templates.sections.timelineMilestones' },
      { key: 'resources', titleKey: 'templates.sections.resources' },
      { key: 'risks_mitigation', titleKey: 'templates.sections.risksMitigation' },
      { key: 'communication_plan', titleKey: 'templates.sections.communicationPlan' },
    ],
    questions: [
      { id: 'project_name', type: 'text', questionKey: 'templates.projectPlan.questions.projectName', required: true },
      { id: 'project_description', type: 'textarea', questionKey: 'templates.projectPlan.questions.projectDescription', required: true },
      { id: 'project_type', type: 'select', questionKey: 'templates.projectPlan.questions.projectType', options: ['software', 'construction', 'event', 'research', 'other'], required: true },
      { id: 'start_date', type: 'text', questionKey: 'templates.projectPlan.questions.startDate', required: true },
      { id: 'end_date', type: 'text', questionKey: 'templates.projectPlan.questions.endDate', required: true },
      { id: 'team_size', type: 'text', questionKey: 'templates.projectPlan.questions.teamSize' },
      { id: 'budget', type: 'text', questionKey: 'templates.projectPlan.questions.budget' },
      { id: 'stakeholders', type: 'textarea', questionKey: 'templates.projectPlan.questions.stakeholders' },
    ],
  },
  {
    id: 'it-roadmap',
    category: PLAN_CATEGORIES.FUNCTIONAL_TACTICAL,
    nameKey: 'templates.itRoadmap.name',
    descriptionKey: 'templates.itRoadmap.description',
    icon: 'Server',
    sections: [
      { key: 'current_state', titleKey: 'templates.sections.currentState' },
      { key: 'vision_goals', titleKey: 'templates.sections.visionGoals' },
      { key: 'technology_stack', titleKey: 'templates.sections.technologyStack' },
      { key: 'initiatives', titleKey: 'templates.sections.initiatives' },
      { key: 'timeline', titleKey: 'templates.sections.timeline' },
      { key: 'resource_plan', titleKey: 'templates.sections.resourcePlan' },
      { key: 'governance', titleKey: 'templates.sections.governance' },
    ],
    questions: [
      { id: 'organization_name', type: 'text', questionKey: 'templates.itRoadmap.questions.organizationName', required: true },
      { id: 'current_challenges', type: 'textarea', questionKey: 'templates.itRoadmap.questions.currentChallenges', required: true },
      { id: 'focus_areas', type: 'multiselect', questionKey: 'templates.itRoadmap.questions.focusAreas', options: ['infrastructure', 'security', 'cloud', 'data', 'applications', 'automation', 'ai'], required: true },
      { id: 'planning_horizon', type: 'select', questionKey: 'templates.itRoadmap.questions.planningHorizon', options: ['1year', '2year', '3year', '5year'], required: true },
      { id: 'budget_range', type: 'text', questionKey: 'templates.itRoadmap.questions.budgetRange' },
    ],
  },
  {
    id: 'resource-allocation-plan',
    category: PLAN_CATEGORIES.FUNCTIONAL_TACTICAL,
    nameKey: 'templates.resourceAllocationPlan.name',
    descriptionKey: 'templates.resourceAllocationPlan.description',
    icon: 'PieChart',
    sections: [
      { key: 'resource_inventory', titleKey: 'templates.sections.resourceInventory' },
      { key: 'demand_analysis', titleKey: 'templates.sections.demandAnalysis' },
      { key: 'allocation_strategy', titleKey: 'templates.sections.allocationStrategy' },
      { key: 'prioritization', titleKey: 'templates.sections.prioritization' },
      { key: 'monitoring', titleKey: 'templates.sections.monitoring' },
    ],
    questions: [
      { id: 'organization_name', type: 'text', questionKey: 'templates.resourceAllocationPlan.questions.organizationName', required: true },
      { id: 'resource_types', type: 'multiselect', questionKey: 'templates.resourceAllocationPlan.questions.resourceTypes', options: ['human', 'financial', 'equipment', 'technology', 'facilities'], required: true },
      { id: 'planning_period', type: 'select', questionKey: 'templates.resourceAllocationPlan.questions.planningPeriod', options: ['monthly', 'quarterly', 'annual'], required: true },
      { id: 'key_projects', type: 'textarea', questionKey: 'templates.resourceAllocationPlan.questions.keyProjects', required: true },
      { id: 'constraints', type: 'textarea', questionKey: 'templates.resourceAllocationPlan.questions.constraints' },
    ],
  },
  {
    id: 'budget-plan',
    category: PLAN_CATEGORIES.FUNCTIONAL_TACTICAL,
    nameKey: 'templates.budgetPlan.name',
    descriptionKey: 'templates.budgetPlan.description',
    icon: 'Calculator',
    sections: [
      { key: 'revenue_projections', titleKey: 'templates.sections.revenueProjections' },
      { key: 'expense_categories', titleKey: 'templates.sections.expenseCategories' },
      { key: 'capital_expenditures', titleKey: 'templates.sections.capitalExpenditures' },
      { key: 'cash_flow', titleKey: 'templates.sections.cashFlow' },
      { key: 'variance_analysis', titleKey: 'templates.sections.varianceAnalysis' },
    ],
    questions: [
      { id: 'organization_name', type: 'text', questionKey: 'templates.budgetPlan.questions.organizationName', required: true },
      { id: 'fiscal_year', type: 'text', questionKey: 'templates.budgetPlan.questions.fiscalYear', required: true },
      { id: 'budget_type', type: 'select', questionKey: 'templates.budgetPlan.questions.budgetType', options: ['operating', 'capital', 'project', 'department'], required: true },
      { id: 'previous_budget', type: 'text', questionKey: 'templates.budgetPlan.questions.previousBudget' },
      { id: 'major_initiatives', type: 'textarea', questionKey: 'templates.budgetPlan.questions.majorInitiatives' },
    ],
  },
  {
    id: 'workforce-plan',
    category: PLAN_CATEGORIES.FUNCTIONAL_TACTICAL,
    nameKey: 'templates.workforcePlan.name',
    descriptionKey: 'templates.workforcePlan.description',
    icon: 'UserPlus',
    sections: [
      { key: 'current_workforce', titleKey: 'templates.sections.currentWorkforce' },
      { key: 'future_needs', titleKey: 'templates.sections.futureNeeds' },
      { key: 'gap_analysis', titleKey: 'templates.sections.gapAnalysis' },
      { key: 'recruitment_strategy', titleKey: 'templates.sections.recruitmentStrategy' },
      { key: 'retention_strategy', titleKey: 'templates.sections.retentionStrategy' },
      { key: 'timeline_budget', titleKey: 'templates.sections.timelineBudget' },
    ],
    questions: [
      { id: 'organization_name', type: 'text', questionKey: 'templates.workforcePlan.questions.organizationName', required: true },
      { id: 'current_headcount', type: 'text', questionKey: 'templates.workforcePlan.questions.currentHeadcount', required: true },
      { id: 'growth_projection', type: 'text', questionKey: 'templates.workforcePlan.questions.growthProjection' },
      { id: 'key_roles', type: 'textarea', questionKey: 'templates.workforcePlan.questions.keyRoles', required: true },
      { id: 'hiring_timeline', type: 'select', questionKey: 'templates.workforcePlan.questions.hiringTimeline', options: ['immediate', 'quarterly', 'annual'], required: true },
      { id: 'challenges', type: 'textarea', questionKey: 'templates.workforcePlan.questions.challenges' },
    ],
  },
  {
    id: 'training-plan',
    category: PLAN_CATEGORIES.FUNCTIONAL_TACTICAL,
    nameKey: 'templates.trainingPlan.name',
    descriptionKey: 'templates.trainingPlan.description',
    icon: 'GraduationCap',
    sections: [
      { key: 'training_needs', titleKey: 'templates.sections.trainingNeeds' },
      { key: 'learning_objectives', titleKey: 'templates.sections.learningObjectives' },
      { key: 'training_programs', titleKey: 'templates.sections.trainingPrograms' },
      { key: 'delivery_methods', titleKey: 'templates.sections.deliveryMethods' },
      { key: 'schedule', titleKey: 'templates.sections.schedule' },
      { key: 'evaluation', titleKey: 'templates.sections.evaluation' },
    ],
    questions: [
      { id: 'organization_name', type: 'text', questionKey: 'templates.trainingPlan.questions.organizationName', required: true },
      { id: 'target_audience', type: 'textarea', questionKey: 'templates.trainingPlan.questions.targetAudience', required: true },
      { id: 'skill_gaps', type: 'textarea', questionKey: 'templates.trainingPlan.questions.skillGaps', required: true },
      { id: 'training_format', type: 'multiselect', questionKey: 'templates.trainingPlan.questions.trainingFormat', options: ['classroom', 'online', 'onthejob', 'mentoring', 'workshops'], required: true },
      { id: 'budget', type: 'text', questionKey: 'templates.trainingPlan.questions.budget' },
      { id: 'timeline', type: 'select', questionKey: 'templates.trainingPlan.questions.timeline', options: ['monthly', 'quarterly', 'annual'], required: true },
    ],
  },

  // Contingency & Risk Planning
  {
    id: 'contingency-plan',
    category: PLAN_CATEGORIES.CONTINGENCY_RISK,
    nameKey: 'templates.contingencyPlan.name',
    descriptionKey: 'templates.contingencyPlan.description',
    icon: 'AlertTriangle',
    sections: [
      { key: 'scenario_identification', titleKey: 'templates.sections.scenarioIdentification' },
      { key: 'impact_assessment', titleKey: 'templates.sections.impactAssessment' },
      { key: 'response_strategies', titleKey: 'templates.sections.responseStrategies' },
      { key: 'trigger_points', titleKey: 'templates.sections.triggerPoints' },
      { key: 'resource_requirements', titleKey: 'templates.sections.resourceRequirements' },
      { key: 'communication_plan', titleKey: 'templates.sections.communicationPlan' },
    ],
    questions: [
      { id: 'organization_name', type: 'text', questionKey: 'templates.contingencyPlan.questions.organizationName', required: true },
      { id: 'scenario_type', type: 'multiselect', questionKey: 'templates.contingencyPlan.questions.scenarioType', options: ['economic', 'market', 'operational', 'regulatory', 'competitive', 'technology'], required: true },
      { id: 'critical_operations', type: 'textarea', questionKey: 'templates.contingencyPlan.questions.criticalOperations', required: true },
      { id: 'risk_tolerance', type: 'select', questionKey: 'templates.contingencyPlan.questions.riskTolerance', options: ['low', 'medium', 'high'], required: true },
      { id: 'existing_plans', type: 'textarea', questionKey: 'templates.contingencyPlan.questions.existingPlans' },
    ],
  },
  {
    id: 'risk-management-plan',
    category: PLAN_CATEGORIES.CONTINGENCY_RISK,
    nameKey: 'templates.riskManagementPlan.name',
    descriptionKey: 'templates.riskManagementPlan.description',
    icon: 'ShieldAlert',
    sections: [
      { key: 'risk_identification', titleKey: 'templates.sections.riskIdentification' },
      { key: 'risk_assessment', titleKey: 'templates.sections.riskAssessment' },
      { key: 'risk_prioritization', titleKey: 'templates.sections.riskPrioritization' },
      { key: 'mitigation_strategies', titleKey: 'templates.sections.mitigationStrategies' },
      { key: 'monitoring_review', titleKey: 'templates.sections.monitoringReview' },
      { key: 'roles_responsibilities', titleKey: 'templates.sections.rolesResponsibilities' },
    ],
    questions: [
      { id: 'organization_name', type: 'text', questionKey: 'templates.riskManagementPlan.questions.organizationName', required: true },
      { id: 'scope', type: 'select', questionKey: 'templates.riskManagementPlan.questions.scope', options: ['enterprise', 'project', 'department', 'process'], required: true },
      { id: 'risk_categories', type: 'multiselect', questionKey: 'templates.riskManagementPlan.questions.riskCategories', options: ['strategic', 'operational', 'financial', 'compliance', 'reputational', 'technology'], required: true },
      { id: 'known_risks', type: 'textarea', questionKey: 'templates.riskManagementPlan.questions.knownRisks', required: true },
      { id: 'risk_appetite', type: 'textarea', questionKey: 'templates.riskManagementPlan.questions.riskAppetite' },
    ],
  },
  {
    id: 'disaster-recovery-plan',
    category: PLAN_CATEGORIES.CONTINGENCY_RISK,
    nameKey: 'templates.disasterRecoveryPlan.name',
    descriptionKey: 'templates.disasterRecoveryPlan.description',
    icon: 'LifeBuoy',
    sections: [
      { key: 'business_impact', titleKey: 'templates.sections.businessImpact' },
      { key: 'recovery_objectives', titleKey: 'templates.sections.recoveryObjectives' },
      { key: 'critical_systems', titleKey: 'templates.sections.criticalSystems' },
      { key: 'recovery_procedures', titleKey: 'templates.sections.recoveryProcedures' },
      { key: 'communication_plan', titleKey: 'templates.sections.communicationPlan' },
      { key: 'testing_maintenance', titleKey: 'templates.sections.testingMaintenance' },
    ],
    questions: [
      { id: 'organization_name', type: 'text', questionKey: 'templates.disasterRecoveryPlan.questions.organizationName', required: true },
      { id: 'disaster_types', type: 'multiselect', questionKey: 'templates.disasterRecoveryPlan.questions.disasterTypes', options: ['natural', 'cyber', 'infrastructure', 'pandemic', 'supply_chain'], required: true },
      { id: 'critical_systems', type: 'textarea', questionKey: 'templates.disasterRecoveryPlan.questions.criticalSystems', required: true },
      { id: 'rto', type: 'select', questionKey: 'templates.disasterRecoveryPlan.questions.rto', options: ['hours', 'day', 'days', 'week'], required: true },
      { id: 'rpo', type: 'select', questionKey: 'templates.disasterRecoveryPlan.questions.rpo', options: ['none', 'hours', 'day', 'days'], required: true },
      { id: 'backup_location', type: 'textarea', questionKey: 'templates.disasterRecoveryPlan.questions.backupLocation' },
    ],
  },
];

export const getTemplateById = (id) => PLAN_TEMPLATES.find(t => t.id === id);

export const getTemplatesByCategory = (category) => PLAN_TEMPLATES.filter(t => t.category === category);
